import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { initializeErrorTracking } from './services/errorTracking';
import { initWebVitals, WebVitalMetric } from './utils/performance';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import VerifyEmailPending from './pages/VerifyEmailPending';
import Dashboard from './pages/Dashboard';
import AgentList from './pages/AgentList';
import AgentDetail from './pages/AgentDetail';
import AgentBuilder from './pages/AgentBuilder';
import ExecutionHistory from './pages/ExecutionHistory';
import ExecutionDetailPage from './pages/ExecutionDetailPage';
import WorkflowList from './pages/WorkflowList';
import WorkflowBuilder from './pages/WorkflowBuilder';
import WorkflowDetail from './pages/WorkflowDetail';
import WorkflowExecutionDetailPage from './pages/WorkflowExecutionDetailPage';
import TemplateMarketplace from './pages/TemplateMarketplace';
import Billing from './pages/Billing';
import AccountSettings from './pages/AccountSettings';
import Organizations from './pages/Organizations';
import Schedules from './pages/Schedules';
import Admin from './pages/Admin';
import Landing from './pages/Landing';
import { Terms, Privacy, Refund, Contact, About } from './pages/Legal';
import { ComparisonsIndex, ComparePage } from './pages/Compare';
import { SolutionsIndex, SolutionPage } from './pages/Solutions';
import { HelpIndex, HelpArticlePage } from './pages/Help';
import { BlogIndex, BlogArticlePage } from './pages/Blog';
import { DocsIndex, DocsPage } from './pages/Docs';
import ExampleRun from './pages/ExampleRun';
import TemplateAnalytics from './pages/TemplateAnalytics';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import ExecutionAnalytics from './pages/ExecutionAnalytics';
import CostAnalytics from './pages/CostAnalytics';
import SystemHealth from './pages/SystemHealth';
import ErrorPage, { NotFoundPage } from './pages/ErrorPage';

// Initialize error tracking
initializeErrorTracking();

// Initialize Web Vitals monitoring
initWebVitals((metric: WebVitalMetric) => {
  // Log metrics in development
  if (import.meta.env.DEV) {
    console.log(`[Web Vitals] ${metric.name}:`, {
      value: metric.value.toFixed(2),
      rating: metric.rating,
    });
  }

  // In production, send to analytics endpoint
  // TODO: Send to backend analytics
  // api.post('/analytics/vitals', metric);
});

// Protected Route Component - redirects to /login when not authenticated
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Home Route - public marketing landing for logged-out visitors; authed users
// go straight to their dashboard.
function HomeRoute() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <Landing />;
}

// Admin Route - requires an authenticated ADMIN/SUPER_ADMIN; others 404
function AdminRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role !== 'ADMIN' && user?.role !== 'SUPER_ADMIN') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function AppContent() {
  const initialize = useAuthStore((state) => state.initialize);
  const [initialized, setInitialized] = useState(false);

  // Validate any persisted token on app load
  useEffect(() => {
    initialize().finally(() => setInitialized(true));
  }, [initialize]);

  if (!initialized) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-gray-500">Loading…</div>
      </div>
    );
  }

  return (
    <main>
    <Routes>
      {/* Public authentication routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/verify-email/:token" element={<VerifyEmail />} />
      <Route path="/verify-email-pending" element={<VerifyEmailPending />} />

      {/* Public marketing + legal */}
      <Route path="/" element={<HomeRoute />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/refund" element={<Refund />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/about" element={<About />} />
      <Route path="/compare" element={<ComparisonsIndex />} />
      <Route path="/compare/:slug" element={<ComparePage />} />
      <Route path="/solutions" element={<SolutionsIndex />} />
      <Route path="/solutions/:slug" element={<SolutionPage />} />
      <Route path="/example" element={<ExampleRun />} />
      <Route path="/help" element={<HelpIndex />} />
      <Route path="/help/:slug" element={<HelpArticlePage />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogArticlePage />} />
      <Route path="/docs" element={<DocsIndex />} />
      <Route path="/docs/:slug" element={<DocsPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents"
        element={
          <ProtectedRoute>
            <AgentList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents/new"
        element={
          <ProtectedRoute>
            <AgentBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents/:id"
        element={
          <ProtectedRoute>
            <AgentDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents/:id/edit"
        element={
          <ProtectedRoute>
            <AgentBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/executions"
        element={
          <ProtectedRoute>
            <ExecutionHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/executions/:id"
        element={
          <ProtectedRoute>
            <ExecutionDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workflows"
        element={
          <ProtectedRoute>
            <WorkflowList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workflows/new"
        element={
          <ProtectedRoute>
            <WorkflowBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workflows/:id"
        element={
          <ProtectedRoute>
            <WorkflowDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workflows/:id/edit"
        element={
          <ProtectedRoute>
            <WorkflowBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workflows/:workflowId/executions/:executionId"
        element={
          <ProtectedRoute>
            <WorkflowExecutionDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates"
        element={
          <ProtectedRoute>
            <TemplateMarketplace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates/analytics"
        element={
          <ProtectedRoute>
            <TemplateAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <Billing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <AccountSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizations"
        element={
          <ProtectedRoute>
            <Organizations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedules"
        element={
          <ProtectedRoute>
            <Schedules />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <Admin />
          </AdminRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <AnalyticsDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/executions"
        element={
          <ProtectedRoute>
            <ExecutionAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/costs"
        element={
          <ProtectedRoute>
            <CostAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/system"
        element={
          /* Admin-only: reports live connection counts and infra shape. */
          <AdminRoute>
            <SystemHealth />
          </AdminRoute>
        }
      />
      {/* Error pages */}
      <Route path="/error" element={<ErrorPage />} />
      <Route path="/404" element={<NotFoundPage />} />

      {/* Catch-all route - 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </main>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
